import { z } from 'zod';
import { FINANCE_TASK_STATUSES, LATE_ONLINE_PAYMENT_MODES, STAFF_ROLES } from '@bulk/types';
import { idSchema, patchOf } from './common';
import { monthSchema } from './timesheets';

export const createFinanceDeadlineSchema = z.object({
  title: z.string().trim().min(1).max(120),
  note: z.string().trim().max(1000).nullable().default(null),
  dayOfMonth: z.number().int().min(1).max(31).describe('A day past the end of a short month falls on its last day, so 31 means the last day of every month'),
  responsibleRole: z.enum(STAFF_ROLES).nullable().default(null).describe('Everyone with this role in the gym answers for it; ignored when responsibleUserId is set'),
  responsibleUserId: idSchema.nullable().default(null),
  remindDaysBefore: z
    .array(z.number().int().min(0).max(28))
    .max(5)
    .default([3, 0])
    .transform((days) => [...new Set(days)].sort((a, b) => b - a))
    .describe('0 reminds on the day itself'),
  active: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});
export type CreateFinanceDeadlineInput = z.infer<typeof createFinanceDeadlineSchema>;

export const updateFinanceDeadlineSchema = patchOf(createFinanceDeadlineSchema);
export type UpdateFinanceDeadlineInput = z.infer<typeof updateFinanceDeadlineSchema>;

export const financeDeadlineSchema = z.object({
  id: idSchema,
  title: z.string(),
  note: z.string().nullable(),
  dayOfMonth: z.number().int(),
  responsibleRole: z.enum(STAFF_ROLES).nullable(),
  responsibleUserId: idSchema.nullable(),
  responsibleName: z.string().nullable(),
  remindDaysBefore: z.array(z.number().int()),
  active: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
});
export type FinanceDeadlineDto = z.infer<typeof financeDeadlineSchema>;

export const financeTaskSchema = z.object({
  id: idSchema,
  deadlineId: idSchema,
  month: z.string(),
  dueDate: z.string(),
  title: z.string(),
  note: z.string().nullable().describe("The deadline's standing note"),
  responsibleRole: z.enum(STAFF_ROLES).nullable(),
  responsibleName: z.string().nullable(),
  status: z.enum(FINANCE_TASK_STATUSES),
  overdue: z.boolean(),
  daysLeft: z.number().int().describe("Days from today to the due date in the gym's time zone; negative once it has passed"),
  completedAt: z.string().nullable(),
  completedByName: z.string().nullable(),
  completionNote: z.string().nullable(),
  attachment: z.object({ fileName: z.string(), mimeType: z.string(), sizeBytes: z.number().int() }).nullable(),
});
export type FinanceTaskDto = z.infer<typeof financeTaskSchema>;

export const financeMonthCloseSchema = z.object({
  closedAt: z.string(),
  closedByName: z.string().nullable(),
  reopenedAt: z.string().nullable(),
  reopenedByName: z.string().nullable(),
  reopenReason: z.string().nullable(),
});
export type FinanceMonthCloseDto = z.infer<typeof financeMonthCloseSchema>;

export const afterClosePaymentSchema = z.object({
  paymentId: idSchema,
  memberName: z.string().nullable(),
  amountMinor: z.number().int(),
  currency: z.string(),
  paidAt: z.string().nullable().describe("Where the payment sits in the books: the provider's date when flagged, or the start of the next open month"),
  description: z.string().nullable(),
});
export type AfterClosePaymentDto = z.infer<typeof afterClosePaymentSchema>;

export const financeMonthSchema = z.object({
  month: z.string(),
  today: z.string(),
  tasks: z.array(financeTaskSchema),
  open: z.number().int(),
  overdue: z.number().int(),
  done: z.number().int(),
  skipped: z.number().int(),
  ended: z.boolean().describe("Whether the month is over in the gym's time zone; only an ended month can be closed"),
  closed: financeMonthCloseSchema.nullable().describe('The close in force, null while the month is open'),
  history: z.array(financeMonthCloseSchema).describe('Every close of this month, newest first, reopened ones included'),
  afterClose: z.array(afterClosePaymentSchema).describe('Online payments the provider dated in this month but confirmed after it was closed'),
  lateOnlinePayments: z.enum(LATE_ONLINE_PAYMENT_MODES),
});
export type FinanceMonthDto = z.infer<typeof financeMonthSchema>;

export const financeMonthQuerySchema = z.object({ month: monthSchema.optional() });

export const reopenFinanceMonthSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export type ReopenFinanceMonthInput = z.infer<typeof reopenFinanceMonthSchema>;

export const financeCloseSettingsSchema = z.object({ lateOnlinePayments: z.enum(LATE_ONLINE_PAYMENT_MODES) });
export type FinanceCloseSettingsInput = z.infer<typeof financeCloseSettingsSchema>;

export const completeFinanceTaskSchema = z.object({
  status: z.enum(['DONE', 'SKIPPED']),
  note: z.string().trim().max(1000).nullable().default(null),
});
export type CompleteFinanceTaskInput = z.infer<typeof completeFinanceTaskSchema>;

export const financeTaskLinkSchema = z.object({ url: z.string(), fileName: z.string(), mimeType: z.string() });
export type FinanceTaskLinkDto = z.infer<typeof financeTaskLinkSchema>;

export const financePersonSchema = z.object({ userId: idSchema, name: z.string(), role: z.enum(STAFF_ROLES) });
export type FinancePersonDto = z.infer<typeof financePersonSchema>;
